import  { useCallback, useEffect, useState } from 'react';
import { useCreateReceipt, useGetReceipt, useUpdateReceipt } from '../../../api/LogbookApi';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import moment from 'moment';
import { useTypesUnites } from '../../other/Costsfunctions';
import { GetCostCategories } from '../../other/ConfigBasic';
import useAutoComplete from '../../other/useAutoComplete';
import 'dayjs/locale/de';
import { v4 as uuidv4 } from 'uuid';

export default function useReceiptForm({ id, costCentreList, costObjectList }) {
    const [imageUrlLocal, setImageUrlLocal] = useState(null);
    const [dtBilling, setDtBilling] = useState();
    const [addressChange, setAddressChange] = useState('');
    const [vendor, setVendor] = useState('');
    const [address, setAddress] = useState(null);
    const [number, setNumber] = useState('');
    const [category, setCategory] = useState([]);
    const [total, setTotal] = useState('');
    const [vat, setVat] = useState(19);
    const [type, setType] = useState('');
    const [quantity, setQuantity] = useState('');
    const [unitPrice, setUnitPrice] = useState('');
    const [unit, setUnit] = useState('');
    const [costCentre, setCostCentre] = useState('');
    const [costObject, setCostObject] = useState('');
    const [userCleared, setUserCleared] = useState(false);
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState('');
    const [isPdf, setIsPdf] = useState(false);
    const categories = GetCostCategories();
    const userAccess = useOutletContext().userAccess;
    const [validateError, setValidateError] = useState({
        vendor: false,
        address: false,
        unitPrice: false,
        number: false,
        quantity: false,
        fuel: false,
        total: false,
        vat: false,
    });
    const typeUnits = useTypesUnites();
    const idLogbook = useOutletContext().logbookId;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { createReceipt, loading } = useCreateReceipt(setMessage);
    const { receipt, loading: receiptLoading, error: receiptError } = useGetReceipt(id);
    const { updateReceipt } = useUpdateReceipt(setMessage);
    const [selectedOption, setSelectedOption] = useState(null);
    const { options } = useAutoComplete(addressChange, setAddress, setMessage);
    /** A Function to set the validation of the data the being sent */
    const formValidation = (setValidState) => {
        let validData = true;
        const updatedValidateError = {
            vendor: false,
            address: false,
            unitPrice: false,
            number: false,
            quantity: false,
            fuel: false,
            total: false,
            vat: false,
        };

        if (vendor?.length === 0) {
            setMessage(t('verndorFehler'));
            updatedValidateError.vendor = true;
            validData = false;
        }
        if (addressChange?.length === 0) {
            setMessage(t('addressFehler'));
            updatedValidateError.address = true;
            validData = false;
        }
        if (unitPrice <= 0) {
            setMessage(t('unitPriceFehler'));
            updatedValidateError.unitPrice = true;
            validData = false;
        }
        if (number?.length === 0) {
            setMessage(t('receiptNumberFehler'));
            updatedValidateError.number = true;
            validData = false;
        }
        if (quantity <= 0) {
            setMessage(t('quantityFehler'));
            updatedValidateError.quantity = true;
            validData = false;
        }
        if (category[0] === categories.FUEL && type?.length === 0) {
            setMessage(t('typeFehler'));
            updatedValidateError.fuel = true;
            validData = false;
        }
        if (total <= 0) {
            setMessage(t('totalFehler'));
            updatedValidateError.total = true;
            validData = false;
        }
        if (vat <= 0) {
            setMessage(t('vatFehler'));
            updatedValidateError.vat = true;
            validData = false;
        }
        setValidState(updatedValidateError); // Update the state with the new object

        return validData;
    };
    const checkCostCentre = (costCentreList, paramCostCentre) => {
        const foundedCostCentre = costCentreList.find((costcenter) => costcenter.name === paramCostCentre[0]);
        if (foundedCostCentre === undefined) {
            costCentreList.push({
                id: uuidv4(),
                name: paramCostCentre[0],
                active: true,
            });
        }
    };
    const checkCostObject = (costObjectList, paramCostObject) => {
        const foundedCostObject = costObjectList.find((costObject) => costObject.name === paramCostObject[0]);
        if (foundedCostObject === undefined) {
            costObjectList.push({
                id: uuidv4(),
                name: paramCostObject[0],
                active: true,
            });
        }
    };
    const handleEditCreateReceipt = () => {
        navigate('/costsOverview');
    };

    useEffect(() => {
        if (options.length > 0 && !selectedOption && !addressChange && !userCleared) {
            setSelectedOption(options[0]);
            setAddressChange(options[0].label);
        }
    }, [options, addressChange, selectedOption, userCleared]);

    const handleOptionChange = (event, newValue) => {
        setUserCleared(false); // Reset the flag here
        setSelectedOption(newValue);
        if (newValue) {
            setAddressChange(newValue.label);
        } else {
            setAddressChange('');
        }
    };
    /** useEffect to calculate the unit price  */
    useEffect(() => {
        const newTotal = String(total).includes(',') ? total.replace(',', '.') : total;
        const newQuantity = String(quantity).includes(',') ? quantity.replace(',', '.') : quantity;

        const calculatedUnitPrice = newTotal / newQuantity;
        if (!isNaN(calculatedUnitPrice) && isFinite(calculatedUnitPrice)) {
            if (category[0] !== categories.FUEL || type === 'FUEL_ELECTRICITY') {
                setUnitPrice(calculatedUnitPrice.toFixed(2));
            } else {
                setUnitPrice(calculatedUnitPrice.toFixed(3));
            }
        } else {
            setUnitPrice('');
        }
    }, [total, quantity, id, type, categories.FUEL, category]);

    const formatAddress = (addr) => {
        if (!addr) return '';

        const streetLine = [addr.street, addr.houseNo].filter(Boolean).join(' ');
        const cityLine = [addr.zip, addr.city, addr.country].filter(Boolean).join(' ');

        return [streetLine, cityLine].filter(Boolean).join(', ');
    };

    /** A Function for the Edit to set the states to the receipt being edited */
    const handleSetData = useCallback(() => {
        if (!receiptLoading && id && !receiptError) {
            const oneReceipt = receipt?.logbookReceipts[0];
            setVendor(oneReceipt?.vendor);
            if (oneReceipt.imageUrl !== null) {
                setImageUrlLocal(`${process.env.REACT_APP_MINX_API_URL}/${id}`);
                setIsPdf(oneReceipt?.imageUrl?.endsWith('.pdf'));
            }
            const oldAddress = formatAddress(oneReceipt?.address);
            setAddressChange(oldAddress);
            setDtBilling(oneReceipt?.dtBilling);
            setNumber(oneReceipt?.number);
            setTotal(String(oneReceipt?.total).replace('.', ','));
            setNumber(oneReceipt?.number);
            setCategory([oneReceipt?.articles[0]?.category]);
            if (oneReceipt?.articles[0]?.type?.includes('FUEL') || oneReceipt?.articles[0]?.type?.includes('OIL')) {
                const value = oneReceipt?.articles[0]?.type;
                setType(value);
            }
            setVat(oneReceipt?.articles[0]?.vat);
            setQuantity(String(oneReceipt?.articles[0]?.quantity).replace('.', ','));
            setUnitPrice(String(oneReceipt?.articles[0]?.unitPrice).replace('.', ','));
            setFile(null);
            setCostCentre(oneReceipt?.articles[0]?.costCentre === null ? '' : oneReceipt?.articles[0]?.costCentre);
            setCostObject(oneReceipt?.articles[0]?.costObject === null ? '' : oneReceipt?.articles[0]?.costObject);
        }
    }, [id, receiptLoading, receiptError, receipt]);

    // Style für die Input Fielder bei dem zweiten Box
    const inputStylePhone = {
        width: '296px',
        height: '48px',
        '& .css-j08ujv-MuiFormControl-root-MuiTextField-root': {
            height: 'auto',
            bgcolor: 'transparent',
            mb: '0',
        },
        '& .MuiFormHelperText-root': {
            mt: -1,
        },
        fontSize: '14px',
        fontFamily: 'CircularProMedium',
    };

    const beitraginputStylePhone = {
        width: '143.3px',
        height: '48px',
        fontSize: '14px',
        borderRadius: '4px',
        p: 0,
        '& .css-j08ujv-MuiFormControl-root-MuiTextField-root': {
            height: 'auto',
            bgcolor: 'transparent',
            mb: '0',
        },
        '& .MuiFormHelperText-root': {
            mt: -1,
        },

        fontFamily: 'CircularProMedium',
    };

    /** A Function to change the Vaule of the unit */
    const getUnit = (fuelType) => {
        switch (fuelType) {
            case 'FUEL_DIESEL':
                setUnit('LITRE');
                break;
            case 'FUEL_GAS':
                setUnit('LITRE');
                break;
            case 'FUEL_ELECTRICITY':
                setUnit('KWH');
                break;
            case 'FUEL_LPG':
                setUnit('LITRE');
                break;
            case 'FUEL_CNG':
                setUnit('KG');
                break;
            case 'FUEL_H2':
                setUnit('KG');
                break;
            case 'FUEL_ADBLUE':
                setUnit('LITRE');
                break;
            case 'OIL_PURCHASE':
                setUnit('LITRE');
                break;
            default:
                setUnit('PIECE');
                break;
        }
    };

    useEffect(() => {
        getUnit(type);
    }, [type]);

    useEffect(() => {
        handleSetData();
    }, [handleSetData, id]);

    const getFile = () => {
        let sendFile = null;
        if (file !== null) {
            sendFile = new FormData();
            sendFile.append('file', file);
        }
        return sendFile;
    };

    const handleSubmit = async (event) => {
        const currency = 'EUR';
        const netTotal = (Number(total.replace(',', '.')) / 119) * 100;
        const isFormValid = formValidation(setValidateError);

        if (!id && isFormValid) {
            event.preventDefault();
            await createReceipt(
                address,
                currency,
                moment(dtBilling).format(),
                Number(netTotal),
                number,
                Number(total.replace(',', '.')),
                vendor,
                idLogbook,
                Number(quantity.replace(',', '.')),
                type,
                unit,
                Number(unitPrice),
                Number(vat),
                getFile(),
                category[0],
                costCentre !== '' ? costCentre : null,
                costObject !== '' ? costObject : null
            );
        } else if (id && isFormValid) {
            await updateReceipt(
                address,
                currency,
                moment(dtBilling).format(),
                Number(netTotal),
                number,
                Number(total.replace(',', '.')),
                vendor,
                id,
                Number(quantity.replace(',', '.')),
                type,
                unit,
                Number(unitPrice),
                Number(vat),
                getFile(),
                category[0],
                costCentre !== '' ? costCentre : null,
                costObject !== '' ? costObject : null
            );
        }
    };

    if (receipt?.logbookReceipts[0]?.articles[0]?.costCentre && costCentre.length > 0) {
        checkCostCentre(costCentreList, costCentre);
    }

    if (receipt?.logbookReceipts[0]?.articles[0]?.costObject && costObject.length > 0) {
        checkCostObject(costObjectList, costObject);
    }

    return {
        imageUrlLocal, setImageUrlLocal, file, setFile, isPdf, setIsPdf,
        dtBilling, setDtBilling,
        vendor, setVendor, address, setAddress, addressChange, setAddressChange,
        number, setNumber, category, setCategory, total, setTotal, vat, setVat,
        type, setType, quantity, setQuantity, unitPrice, setUnitPrice, unit, setUnit,
        costCentre, setCostCentre, costObject, setCostObject,
        message, setMessage, userCleared, setUserCleared, selectedOption, setSelectedOption, validateError, setValidateError,
        categories, typeUnits, userAccess, idLogbook, options,
        inputStylePhone, beitraginputStylePhone,
        loading, receipt, receiptLoading, receiptError,
        navigate,
        handleSubmit, handleEditCreateReceipt, handleOptionChange, formValidation, getUnit, getFile,
        checkCostCentre, checkCostObject,
    };
}